import { createPortal } from "react-dom";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Check, ChevronDown, ChevronUp, CloudUpload, Loader2, X, AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  classifyAddStudyExtract,
  isAddStudyQueueFile,
  needsAddStudyMismatchReview,
} from "@/lib/addStudyMismatch";
import {
  needsStandaloneNewPatientReview,
  standaloneNewPatientMismatchPatch,
} from "@/lib/uploadMismatch";
import { useUploadProgress } from "@/contexts/UploadProgressContext";
import { Progress } from "@/components/ui/progress";

/** ~4 stacked file rows before the list scrolls. */
const TRAY_LIST_MAX_HEIGHT = "max-h-[14.5rem]";

function formatSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

/** /upload-origin "patient already exists" review (not Add Study mismatch). */
function isUploadExistsReview(file) {
  if (!file?.patient_mismatch || file.status !== "extracted") return false;
  // Status is immutable by file origin / kind — never flip Mismatch ↔ Already exists by page.
  if (file.origin === "add_study") return false;
  if (
    file.mismatch_kind === "add_study_exists"
    || file.mismatch_kind === "add_study_new"
    || file.mismatch_kind === "locked"
  ) {
    return false;
  }
  if (file.mismatch_kind === "exists") return true;
  return file.origin === "upload" && file.patient_details?.exists === true;
}

/** /upload-origin new/unknown patient review (not Add Study mismatch). */
function isStandaloneNewReview(file) {
  if (!file?.patient_mismatch || file.status !== "extracted") return false;
  if (file.origin === "add_study") return false;
  if (
    file.mismatch_kind === "add_study_exists"
    || file.mismatch_kind === "add_study_new"
    || file.mismatch_kind === "locked"
    || file.mismatch_kind === "exists"
  ) {
    return false;
  }
  if (file.mismatch_kind === "new") return true;
  return file.origin === "upload" && file.patient_details?.exists !== true;
}

function statusLabel(file) {
  if (
    (
      file.patient_mismatch
      || needsAddStudyMismatchReview(file)
      || needsStandaloneNewPatientReview(file)
    )
    && file.status === "extracted"
  ) {
    return "";
  }
  if (file.status === "completed") return "Complete";
  if (file.status === "extracted" && file.needs_study_details) return "Enter study details";
  if (
    file.status === "extracted"
    && !file.patient_mismatch
    && (file.origin === "add_study" || file.queue === "add_study")
  ) {
    return "Tap to finish";
  }
  if (file.status === "extracted") return "Ready to save";
  if (file.status === "error") return file.error || "Failed";
  if (file.phase === "extracting" || (file.status === "uploading" && file.progress >= 100)) {
    return "Extracting…";
  }
  if (file.status === "uploading") return `Uploading ${file.progress ?? 0}%`;
  if (file.status === "pending") return "Waiting…";
  return file.status || "";
}

function headerSummary(files) {
  const uploading = files.filter((f) => f.status === "uploading" || f.status === "pending").length;
  const extracting = files.filter((f) => (
    f.phase === "extracting" || (f.status === "uploading" && f.progress >= 100)
  )).length;
  const reviewNeeded = files.filter((f) => (
    f.status === "extracted" && (
      f.patient_mismatch
      || needsAddStudyMismatchReview(f)
      || needsStandaloneNewPatientReview(f)
    )
  ));
  const existsReviews = reviewNeeded.filter(isUploadExistsReview).length;
  const mismatched = reviewNeeded.length - existsReviews;
  const needsDetails = files.filter((f) => (
    f.status === "extracted"
    && !f.patient_mismatch
    && f.needs_study_details
    && !needsAddStudyMismatchReview(f)
    && !needsStandaloneNewPatientReview(f)
  )).length;
  const ready = files.filter((f) => (
    f.status === "extracted"
    && !f.patient_mismatch
    && !f.needs_study_details
    && f.origin !== "add_study"
    && f.queue !== "add_study"
    && !needsStandaloneNewPatientReview(f)
  )).length;
  const done = files.filter((f) => f.status === "completed").length;
  const failed = files.filter((f) => f.status === "error").length;

  if (uploading > 0 && extracting === 0) {
    return uploading === 1 ? "Uploading 1 item" : `Uploading ${uploading} items`;
  }
  if (extracting > 0) {
    return extracting === 1 ? "Extracting 1 item" : `Extracting ${extracting} items`;
  }
  if (existsReviews > 0 && mismatched === 0) {
    return existsReviews === 1
      ? "1 file already exists"
      : `${existsReviews} files already exist`;
  }
  if (mismatched > 0 && existsReviews === 0) {
    return mismatched === 1 ? "1 file has a mismatch" : `${mismatched} files have mismatches`;
  }
  if (reviewNeeded.length > 0) {
    return reviewNeeded.length === 1
      ? "1 file needs review"
      : `${reviewNeeded.length} files need review`;
  }
  if (needsDetails > 0) {
    return needsDetails === 1
      ? "1 file needs study details"
      : `${needsDetails} files need study details`;
  }
  const finishable = files.filter((f) => (
    f.status === "extracted"
    && !f.patient_mismatch
    && !f.needs_study_details
    && (f.origin === "add_study" || f.queue === "add_study")
    && !needsAddStudyMismatchReview(f)
  )).length;
  if (finishable > 0) {
    return finishable === 1
      ? "1 file ready — tap to finish"
      : `${finishable} files ready — tap to finish`;
  }
  if (ready > 0) {
    return ready === 1 ? "1 file ready to save" : `${ready} files ready to save`;
  }
  if (failed > 0 && done === 0) {
    return failed === 1 ? "1 upload failed" : `${failed} uploads failed`;
  }
  if (done > 0) {
    return done === 1 ? "1 upload complete" : `${done} uploads complete`;
  }
  return "Uploads";
}

/**
 * Upload progress tray. Embedded in the desktop sidebar bottom;
 * floating bottom-right on mobile when the sidebar is hidden.
 */
export default function UploadTray({ embedded = false, collapsed = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    files,
    showTray,
    trayMinimized,
    setTrayMinimized,
    dismissTray,
    removeFile,
    updateFile,
    hasActiveWork,
    openPatientMismatchReview,
    openLockedPatientMismatchReview,
    openFinishAddStudyUpload,
  } = useUploadProgress();

  const onUploadPage = location.pathname.startsWith("/upload");

  // Stamp mismatch as soon as extract details are available — don't wait for Add Study to remount.
  useEffect(() => {
    files.forEach((f) => {
      if (f.status !== "extracted") return;

      if (isAddStudyQueueFile(f)) {
        const classified = classifyAddStudyExtract(
          f.locked_patient || { uhid: f.locked_patient_uhid },
          f.patient_details,
          f,
        );
        if (!f.patient_mismatch) {
          if (classified?.patient_mismatch) {
            updateFile(f.id, {
              ...classified,
              queue: f.queue || "add_study",
              locked_patient_uhid: f.locked_patient_uhid || f.locked_patient?.uhid || "",
            });
          }
          return;
        }
        // Other modality has no patient tags — clear a stale mismatch so Add Study can auto-save.
        if (classified && classified.patient_mismatch === false) {
          updateFile(f.id, {
            ...classified,
            queue: f.queue || "add_study",
            locked_patient_uhid: f.locked_patient_uhid || f.locked_patient?.uhid || "",
          });
        }
        return;
      }

      if (f.origin === "add_study") return;
      if (
        f.mismatch_kind === "add_study_exists"
        || f.mismatch_kind === "add_study_new"
        || f.mismatch_kind === "locked"
        || f.queue === "add_study"
        || f.locked_patient_uhid
      ) {
        return;
      }

      if (!f.patient_mismatch && f.patient_details?.exists === true && f.origin === "upload") {
        const systemUhid = String(f.patient_details.system_uhid || "").trim();
        if (!systemUhid) return;
        updateFile(f.id, {
          patient_mismatch: true,
          mismatch_kind: "exists",
          mismatch_patient_uhid: systemUhid,
          queue: "upload",
          locked_patient_uhid: "",
        });
        return;
      }

      if (!f.patient_mismatch && needsStandaloneNewPatientReview(f)) {
        updateFile(f.id, standaloneNewPatientMismatchPatch());
        return;
      }

      // Heal /upload-origin files restamped as Add Study when opened on a patient page.
      if (
        f.patient_mismatch
        && f.origin === "upload"
        && f.mismatch_kind !== "exists"
        && f.patient_details?.exists === true
      ) {
        const systemUhid = String(
          f.patient_details.system_uhid || f.mismatch_patient_uhid || "",
        ).trim();
        updateFile(f.id, {
          mismatch_kind: "exists",
          mismatch_patient_uhid: systemUhid,
          queue: "upload",
          locked_patient_uhid: "",
        });
      }
    });
  }, [files, updateFile]);

  if (!showTray || files.length === 0) return null;
  if (!embedded && typeof document === "undefined") return null;

  const title = headerSummary(files);
  const overallProgress = (() => {
    const active = files.filter((f) => f.status === "uploading" || f.status === "pending");
    if (active.length === 0) return 100;
    const sum = active.reduce((acc, f) => acc + (f.progress ?? 0), 0);
    return Math.round(sum / active.length);
  })();

  const handleToggleMinimized = (e) => {
    e?.stopPropagation?.();
    setTrayMinimized((prev) => !prev);
  };

  const handleDismiss = (e) => {
    e?.stopPropagation?.();
    dismissTray();
  };

  const handleOpenMismatchReview = (f) => {
    if (!f.patient_mismatch && needsAddStudyMismatchReview(f)) {
      const classified = classifyAddStudyExtract(
        f.locked_patient || { uhid: f.locked_patient_uhid },
        f.patient_details,
        f,
      );
      if (classified?.patient_mismatch) {
        updateFile(f.id, {
          ...classified,
          queue: f.queue || "add_study",
          locked_patient_uhid: f.locked_patient_uhid || f.locked_patient?.uhid || "",
        });
      }
    }

    if (!f.patient_mismatch && needsStandaloneNewPatientReview(f)) {
      updateFile(f.id, standaloneNewPatientMismatchPatch());
    }

    // /upload-origin reviews — open the modal wherever you are (global host
    // mounts when not on /upload). Never navigate away.
    if (isUploadExistsReview(f) || isStandaloneNewReview(f) || f.origin === "upload") {
      openPatientMismatchReview(f.id);
      return;
    }

    // Add Study mismatches: review on the locked patient (who we uploaded for).
    if (onUploadPage) {
      openPatientMismatchReview(f.id);
      return;
    }
    const patientUhid = (
      f.locked_patient_uhid?.trim()
      || f.mismatch_patient_uhid?.trim()
      || ""
    );
    const onMatchingPatientPage = Boolean(
      patientUhid
      && (
        location.pathname.startsWith(`/patients/${encodeURIComponent(patientUhid)}`)
        || location.pathname.startsWith(`/patients/${patientUhid}`)
      ),
    );
    if (patientUhid && !onMatchingPatientPage) {
      navigate(`/patients/${encodeURIComponent(patientUhid)}`);
    }
    openLockedPatientMismatchReview(f.id);
  };

  const handleOpenUploadedPatient = (f) => {
    const patientUhid = (
      f.uploaded_patient_uhid?.trim()
      || f.locked_patient_uhid?.trim()
      || f.mismatch_patient_uhid?.trim()
      || f.patient_details?.system_uhid?.trim()
      || ""
    );
    if (!patientUhid) return;
    navigate(`/patients/${encodeURIComponent(patientUhid)}`);
  };

  const isAddStudyFinishable = (f) => (
    f?.status === "extracted"
    && !f?.patient_mismatch
    && isAddStudyQueueFile(f)
    && !needsAddStudyMismatchReview(f)
  );

  const handleFinishAddStudyUpload = (f) => {
    if (needsAddStudyMismatchReview(f)) {
      handleOpenMismatchReview(f);
      return;
    }
    const patientUhid = (
      f.locked_patient_uhid?.trim()
      || f.mismatch_patient_uhid?.trim()
      || ""
    );
    const onMatchingPatientPage = Boolean(
      patientUhid
      && (
        location.pathname.startsWith(`/patients/${encodeURIComponent(patientUhid)}`)
        || location.pathname.startsWith(`/patients/${patientUhid}`)
      ),
    );
    if (patientUhid && !onMatchingPatientPage) {
      navigate(`/patients/${encodeURIComponent(patientUhid)}`);
    }
    openFinishAddStudyUpload(f.id);
  };

  const compact = embedded && collapsed;
  const fileList = !trayMinimized ? (
    <div className={cn("bg-white", compact && "absolute left-full bottom-0 z-[100] ml-2 w-[280px] rounded-lg border border-[var(--xl-line)] shadow-xl overflow-hidden")}>
      <ul className={cn(TRAY_LIST_MAX_HEIGHT, "overflow-y-auto overscroll-contain divide-y divide-[var(--xl-line)]")}>
        {files.map((f) => {
          const needsReview = (
            f.status === "extracted"
            && (
              f.patient_mismatch
              || needsAddStudyMismatchReview(f)
              || needsStandaloneNewPatientReview(f)
            )
          );
          const needsStudyDetails = f.status === "extracted"
            && !needsReview
            && f.needs_study_details;
          const canFinishAddStudy = isAddStudyFinishable(f);
          const isUploaded = f.status === "completed";
          const rowClickable = needsReview || isUploaded || canFinishAddStudy;

          return (
          <li
            key={f.id}
            className={`px-2.5 py-2 ${
              rowClickable ? "cursor-pointer hover:bg-[var(--xl-bg)]/80" : ""
            }`}
            data-testid={`upload-tray-item-${f.id}`}
            role={rowClickable ? "button" : undefined}
            tabIndex={rowClickable ? 0 : undefined}
            onClick={rowClickable ? () => {
              if (needsReview) handleOpenMismatchReview(f);
              else if (canFinishAddStudy) handleFinishAddStudyUpload(f);
              else if (isUploaded) handleOpenUploadedPatient(f);
            } : undefined}
            onKeyDown={rowClickable ? (e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              if (needsReview) handleOpenMismatchReview(f);
              else if (canFinishAddStudy) handleFinishAddStudyUpload(f);
              else if (isUploaded) handleOpenUploadedPatient(f);
            } : undefined}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="flex-shrink-0 pointer-events-none">
                {f.status === "completed" && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                {f.status === "error" && <AlertCircle className="w-3.5 h-3.5 text-red-600" />}
                {(f.status === "uploading" || f.status === "pending") && (
                  <Loader2 className="w-3.5 h-3.5 text-[var(--xl-primary)] animate-spin" />
                )}
                {needsReview && (
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                )}
                {f.status === "extracted" && !needsReview && needsStudyDetails && (
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                )}
                {f.status === "extracted" && !needsReview && !needsStudyDetails && (
                  <Check className="w-3.5 h-3.5 text-[var(--xl-primary)]" />
                )}
              </div>
              <p className="flex-1 min-w-0 text-sm font-medium truncate pointer-events-none">
                {f.name}
              </p>
              <button
                type="button"
                className="w-6 h-6 grid place-items-center rounded text-[var(--xl-mut)] hover:bg-[var(--xl-bg)] cursor-pointer flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(f.id);
                }}
                aria-label={
                  f.status === "uploading" || f.status === "pending"
                    ? `Cancel ${f.name}`
                    : `Remove ${f.name}`
                }
              >
                <X className="w-3.5 h-3.5 pointer-events-none" />
              </button>
            </div>
            <div className="flex items-center gap-1.5 min-w-0 mt-1 pl-5">
              <p className="flex-1 min-w-0 text-xs text-[var(--xl-mut)] truncate pointer-events-none">
                {[formatSize(f.size), statusLabel(f)].filter(Boolean).join(" · ")}
              </p>
              {needsReview ? (
                <span
                  className="inline-flex h-6 shrink-0 items-center rounded-md border border-amber-300 bg-amber-50 px-1.5 text-[10px] font-medium text-amber-900 pointer-events-none whitespace-nowrap"
                  data-testid={`upload-tray-mismatch-${f.id}`}
                >
                  {isUploadExistsReview(f) ? "Already exists" : "Mismatch"}
                </span>
              ) : null}
              {needsStudyDetails || canFinishAddStudy ? (
                <span
                  className="inline-flex h-6 shrink-0 items-center rounded-md border border-amber-300 bg-amber-50 px-1.5 text-[10px] font-medium text-amber-900 pointer-events-none whitespace-nowrap"
                  data-testid={`upload-tray-needs-details-${f.id}`}
                >
                  {needsStudyDetails ? "Needs details" : "Continue"}
                </span>
              ) : null}
              {isUploaded ? (
                <span
                  className="inline-flex h-6 shrink-0 items-center rounded-md border border-emerald-300 bg-emerald-50 px-1.5 text-[10px] font-medium text-emerald-900 pointer-events-none whitespace-nowrap"
                  data-testid={`upload-tray-uploaded-${f.id}`}
                >
                  Uploaded
                </span>
              ) : null}
            </div>
            {f.status === "uploading" && (
              <Progress value={f.progress ?? 0} className="h-1.5 mt-1.5 pointer-events-none" />
            )}
          </li>
          );
        })}
      </ul>
    </div>
  ) : null;

  const tray = (
    <div
      className={cn(
        "pointer-events-auto bg-white text-[var(--xl-text)] border border-[var(--xl-line)]",
        embedded
          ? cn(
              "relative z-20 w-full flex-shrink-0 rounded-lg shadow-sm mb-3",
              compact ? "overflow-visible" : "overflow-hidden",
            )
          : "overflow-hidden fixed bottom-0 right-0 sm:bottom-4 sm:right-4 z-[10000] w-full sm:w-[360px] shadow-xl sm:rounded-lg",
      )}
      data-testid="upload-tray"
      role="status"
      aria-live="polite"
    >
      <div className={cn(
        "relative z-10 flex items-center gap-1 py-2 bg-[#1f1f1f] text-white",
        compact ? "px-1 justify-center" : "px-2 sm:px-3",
      )}>
        <button
          type="button"
          className={cn(
            "min-w-0 flex items-center gap-2 text-left px-1 py-1 rounded hover:bg-white/5",
            compact ? "flex-none justify-center" : "flex-1",
          )}
          onClick={handleToggleMinimized}
          aria-expanded={!trayMinimized}
          aria-label={trayMinimized ? "Expand upload progress" : "Minimize upload progress"}
          data-testid="upload-tray-toggle"
        >
          {hasActiveWork ? (
            <Loader2 className="w-4 h-4 animate-spin flex-shrink-0 text-white/90 pointer-events-none" />
          ) : (
            <CloudUpload className="w-4 h-4 flex-shrink-0 text-white/90 pointer-events-none" />
          )}
          {!compact && (
            <span className="text-sm font-medium truncate" title={title}>
              {title}
            </span>
          )}
        </button>
        {!compact && (
          <div className="relative z-20 flex items-center flex-shrink-0">
            <button
              type="button"
              className="w-8 h-8 grid place-items-center rounded text-white/80 hover:bg-white/10 hover:text-white cursor-pointer"
              onClick={handleToggleMinimized}
              aria-label={trayMinimized ? "Expand" : "Minimize"}
              data-testid="upload-tray-minimize"
            >
              {trayMinimized
                ? <ChevronUp className="w-4 h-4 pointer-events-none" />
                : <ChevronDown className="w-4 h-4 pointer-events-none" />}
            </button>
            <button
              type="button"
              className="w-8 h-8 grid place-items-center rounded text-white/80 hover:bg-white/10 hover:text-white cursor-pointer"
              onClick={handleDismiss}
              aria-label="Close upload progress"
              data-testid="upload-tray-close"
            >
              <X className="w-4 h-4 pointer-events-none" />
            </button>
          </div>
        )}
      </div>

      {trayMinimized && hasActiveWork && (
        <div className={cn("bg-white", compact ? "px-1.5 py-1.5" : "px-3 py-2")}>
          <Progress value={overallProgress} className="h-1.5" />
        </div>
      )}

      {fileList}
    </div>
  );

  if (embedded) return tray;
  return createPortal(tray, document.body);
}
